
// src/services/database.ts - OPTIMIZED WITH CACHING
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { ThemeTokens } from "../constants/themePresets";
import { NicheOverride } from "../constants/niches";
import {
  Project,
  Skill,
  Service,
  About,
  Settings,
  Testimonial,
  Category,
  ReviewToken,
  Experience,
} from "../types";
import { cache } from "./cache";
import { logger } from "../utils/logger";

// Cache keys
const CACHE_KEYS = {
  ABOUT: "about",
  PROJECTS: (visible?: boolean) => `projects_${visible ? "visible" : "all"}`,
  SKILLS: "skills",
  SERVICES: "services",
  TESTIMONIALS: "testimonials",
  CATEGORIES: "categories",
  SETTINGS: "settings",
  EXPERIENCE: "experience",
};

// About operations
export const getAbout = async (): Promise<About | null> => {
  try {
    const cached = cache.get<About>(CACHE_KEYS.ABOUT);
    if (cached) {
      logger.info("Using cached about data");
      return cached;
    }

    logger.info("Fetching about data from Firestore");
    const docRef = doc(db, "about", "main");
    const docSnap = await getDoc(docRef);
    const result = docSnap.exists() ? (docSnap.data() as About) : null;

    if (result) {
      cache.set(CACHE_KEYS.ABOUT, result);
    }

    return result;
  } catch (error) {
    logger.error("Error fetching about data:", error);
    return null;
  }
};

export const updateAbout = async (data: About): Promise<void> => {
  try {
    const docRef = doc(db, "about", "main");
    await setDoc(docRef, data);
    cache.invalidate(CACHE_KEYS.ABOUT);
    logger.success("About data updated successfully");
  } catch (error) {
    logger.error("Error updating about data:", error);
    throw error;
  }
};

// Projects operations with better error handling
export const getProjects = async (visibleOnly: boolean = false): Promise<Project[]> => {
  try {
    const cacheKey = CACHE_KEYS.PROJECTS(visibleOnly);
    const cached = cache.get<Project[]>(cacheKey);
    if (cached) {
      logger.info("Using cached projects data");
      return cached;
    }

    logger.info("Fetching projects from Firestore");
    const projectsRef = collection(db, "projects");

    let querySnapshot;
    try {
      let q;
      if (visibleOnly) {
        q = query(projectsRef, where("visible", "==", true), orderBy("createdAt", "desc"));
      } else {
        q = query(projectsRef, orderBy("createdAt", "desc"));
      }
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      logger.warn("OrderBy failed, trying without ordering:", orderError);
      let q;
      if (visibleOnly) {
        q = query(projectsRef, where("visible", "==", true));
      } else {
        q = projectsRef;
      }
      querySnapshot = await getDocs(q);
    }

    const projects = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Project[];

    projects.sort((a, b) => {
      const dateA = new Date(a.createdAt || "");
      const dateB = new Date(b.createdAt || "");
      return dateB.getTime() - dateA.getTime();
    });

    cache.set(cacheKey, projects);
    return projects;
  } catch (error) {
    logger.error("Error fetching projects:", error);
    return [];
  }
};

export const addProject = async (project: Omit<Project, "id">): Promise<string> => {
  try {
    const projectsRef = collection(db, "projects");
    const docRef = await addDoc(projectsRef, {
      ...project,
      createdAt: new Date().toISOString(),
    });
    cache.invalidatePattern("projects");
    logger.success("Project added:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error adding project:", error);
    throw error;
  }
};

export const updateProject = async (id: string, project: Partial<Project>): Promise<void> => {
  try {
    const docRef = doc(db, "projects", id);
    await updateDoc(docRef, project);
    cache.invalidatePattern("projects");
    logger.success("Project updated:", id);
  } catch (error) {
    logger.error("Error updating project:", error);
    throw error;
  }
};

export const deleteProject = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "projects", id);
    await deleteDoc(docRef);
    cache.invalidatePattern("projects");
    logger.success("Project deleted:", id);
  } catch (error) {
    logger.error("Error deleting project:", error);
    throw error;
  }
};

// Skills operations
export const getSkills = async (): Promise<Skill[]> => {
  try {
    const cached = cache.get<Skill[]>(CACHE_KEYS.SKILLS);
    if (cached) {
      logger.info("Using cached skills data");
      return cached;
    }

    logger.info("Fetching skills from Firestore");
    const skillsRef = collection(db, "skills");

    try {
      const q = query(skillsRef, orderBy("category"), orderBy("name"));
      const querySnapshot = await getDocs(q);
      const skills = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Skill[];
      cache.set(CACHE_KEYS.SKILLS, skills);
      return skills;
    } catch (orderError) {
      logger.warn("Skills orderBy failed, trying without ordering");
      const querySnapshot = await getDocs(skillsRef);
      const skills = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Skill[];
      cache.set(CACHE_KEYS.SKILLS, skills);
      return skills;
    }
  } catch (error) {
    logger.error("Error fetching skills:", error);
    return [];
  }
};

export const addSkill = async (skill: Omit<Skill, "id">): Promise<string> => {
  try {
    const skillsRef = collection(db, "skills");
    const docRef = await addDoc(skillsRef, skill);
    cache.invalidate(CACHE_KEYS.SKILLS);
    logger.success("Skill added:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error adding skill:", error);
    throw error;
  }
};

export const updateSkill = async (id: string, skill: Partial<Skill>): Promise<void> => {
  try {
    const docRef = doc(db, "skills", id);
    await updateDoc(docRef, skill);
    cache.invalidate(CACHE_KEYS.SKILLS);
    logger.success("Skill updated:", id);
  } catch (error) {
    logger.error("Error updating skill:", error);
    throw error;
  }
};

export const deleteSkill = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "skills", id);
    await deleteDoc(docRef);
    cache.invalidate(CACHE_KEYS.SKILLS);
    logger.success("Skill deleted:", id);
  } catch (error) {
    logger.error("Error deleting skill:", error);
    throw error;
  }
};

// Services operations
export const getServices = async (): Promise<Service[]> => {
  try {
    const cached = cache.get<Service[]>(CACHE_KEYS.SERVICES);
    if (cached) {
      logger.info("Using cached services data");
      return cached;
    }

    logger.info("Fetching services from Firestore");
    const servicesRef = collection(db, "services");
    const querySnapshot = await getDocs(servicesRef);
    const services = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Service[];
    cache.set(CACHE_KEYS.SERVICES, services);
    return services;
  } catch (error) {
    logger.error("Error fetching services:", error);
    return [];
  }
};

export const addService = async (service: Omit<Service, "id">): Promise<string> => {
  try {
    const servicesRef = collection(db, "services");
    const docRef = await addDoc(servicesRef, service);
    cache.invalidate(CACHE_KEYS.SERVICES);
    logger.success("Service added:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error adding service:", error);
    throw error;
  }
};

export const updateService = async (id: string, service: Partial<Service>): Promise<void> => {
  try {
    const docRef = doc(db, "services", id);
    await updateDoc(docRef, service);
    cache.invalidate(CACHE_KEYS.SERVICES);
    logger.success("Service updated:", id);
  } catch (error) {
    logger.error("Error updating service:", error);
    throw error;
  }
};

export const deleteService = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "services", id);
    await deleteDoc(docRef);
    cache.invalidate(CACHE_KEYS.SERVICES);
    logger.success("Service deleted:", id);
  } catch (error) {
    logger.error("Error deleting service:", error);
    throw error;
  }
};

// Settings operations
export const getSettings = async (): Promise<Settings | null> => {
  try {
    const cached = cache.get<Settings>(CACHE_KEYS.SETTINGS);
    if (cached) {
      logger.info("Using cached settings data");
      return cached;
    }

    logger.info("Fetching settings from Firestore");
    const docRef = doc(db, "settings", "main");
    const docSnap = await getDoc(docRef);
    const result = docSnap.exists() ? (docSnap.data() as Settings) : null;

    if (result) {
      cache.set(CACHE_KEYS.SETTINGS, result);
    }

    return result;
  } catch (error) {
    logger.error("Error fetching settings:", error);
    return null;
  }
};

export const updateSettings = async (settings: Settings): Promise<void> => {
  try {
    const docRef = doc(db, "settings", "main");
    await setDoc(docRef, settings);
    cache.invalidate(CACHE_KEYS.SETTINGS);
    logger.success("Settings updated successfully");
  } catch (error) {
    logger.error("Error updating settings:", error);
    throw error;
  }
};

// Testimonials operations
export const getTestimonials = async (): Promise<Testimonial[]> => {
  try {
    const cached = cache.get<Testimonial[]>(CACHE_KEYS.TESTIMONIALS);
    if (cached) {
      logger.info("Using cached testimonials data");
      return cached;
    }

    logger.info("Fetching testimonials from Firestore");
    const testimonialsRef = collection(db, "testimonials");
    const q = query(testimonialsRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q).catch(async () => {
      // Fallback if createdAt field missing or index not ready
      return await getDocs(testimonialsRef);
    });
    const testimonials = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Testimonial[];
    cache.set(CACHE_KEYS.TESTIMONIALS, testimonials);
    return testimonials;
  } catch (error) {
    logger.error("Error fetching testimonials:", error);
    return [];
  }
};

export const addTestimonial = async (testimonial: Omit<Testimonial, "id">): Promise<string> => {
  try {
    const testimonialsRef = collection(db, "testimonials");
    const docRef = await addDoc(testimonialsRef, testimonial);
    cache.invalidate(CACHE_KEYS.TESTIMONIALS);
    logger.success("Testimonial added:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error adding testimonial:", error);
    throw error;
  }
};

export const updateTestimonial = async (id: string, testimonial: Partial<Testimonial>): Promise<void> => {
  try {
    const docRef = doc(db, "testimonials", id);
    await updateDoc(docRef, testimonial);
    cache.invalidate(CACHE_KEYS.TESTIMONIALS);
    logger.success("Testimonial updated:", id);
  } catch (error) {
    logger.error("Error updating testimonial:", error);
    throw error;
  }
};

export const deleteTestimonial = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "testimonials", id);
    await deleteDoc(docRef);
    cache.invalidate(CACHE_KEYS.TESTIMONIALS);
    logger.success("Testimonial deleted:", id);
  } catch (error) {
    logger.error("Error deleting testimonial:", error);
    throw error;
  }
};

// Categories operations
export const getCategories = async (): Promise<Category[]> => {
  try {
    const cached = cache.get<Category[]>(CACHE_KEYS.CATEGORIES);
    if (cached) {
      logger.info("Using cached categories data");
      return cached;
    }

    logger.info("Fetching categories from Firestore");
    const categoriesRef = collection(db, "categories");

    try {
      const q = query(categoriesRef, orderBy("order"), orderBy("name"));
      const querySnapshot = await getDocs(q);
      const categories = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
      cache.set(CACHE_KEYS.CATEGORIES, categories);
      return categories;
    } catch (orderError) {
      logger.warn("Categories orderBy failed, trying without ordering");
      const querySnapshot = await getDocs(categoriesRef);
      const categories = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
      categories.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
      cache.set(CACHE_KEYS.CATEGORIES, categories);
      return categories;
    }
  } catch (error) {
    logger.error("Error fetching categories:", error);
    return [];
  }
};

export const addCategory = async (category: Omit<Category, "id">): Promise<string> => {
  try {
    const categoriesRef = collection(db, "categories");
    const docRef = await addDoc(categoriesRef, {
      ...category,
      createdAt: new Date().toISOString(),
    });
    cache.invalidate(CACHE_KEYS.CATEGORIES);
    logger.success("Category added:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error adding category:", error);
    throw error;
  }
};

export const updateCategory = async (id: string, category: Partial<Category>): Promise<void> => {
  try {
    const docRef = doc(db, "categories", id);
    await updateDoc(docRef, category);
    cache.invalidate(CACHE_KEYS.CATEGORIES);
    logger.success("Category updated:", id);
  } catch (error) {
    logger.error("Error updating category:", error);
    throw error;
  }
};

export const deleteCategory = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "categories", id);
    await deleteDoc(docRef);
    cache.invalidate(CACHE_KEYS.CATEGORIES);
    logger.success("Category deleted:", id);
  } catch (error) {
    logger.error("Error deleting category:", error);
    throw error;
  }
};

export const reorderCategories = async (categories: Category[]): Promise<void> => {
  try {
    const updatePromises = categories.map((category, index) =>
      updateDoc(doc(db, "categories", category.id), { order: index })
    );
    await Promise.all(updatePromises);
    cache.invalidate(CACHE_KEYS.CATEGORIES);
    logger.success("Categories reordered successfully");
  } catch (error) {
    logger.error("Error reordering categories:", error);
    throw error;
  }
};

// Review Token operations
export const createReviewToken = async (data: Omit<ReviewToken, "id">): Promise<string> => {
  try {
    const tokensRef = collection(db, "reviewTokens");
    const docRef = await addDoc(tokensRef, data);
    logger.success("Review token created:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error creating review token:", error);
    throw error;
  }
};

export const getReviewToken = async (token: string): Promise<ReviewToken | null> => {
  try {
    const tokensRef = collection(db, "reviewTokens");
    const q = query(tokensRef, where("token", "==", token));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const docSnap = snap.docs[0];
    return { id: docSnap.id, ...docSnap.data() } as ReviewToken;
  } catch (error) {
    logger.error("Error fetching review token:", error);
    return null;
  }
};

export const markTokenUsed = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "reviewTokens", id);
    await updateDoc(docRef, { used: true });
    logger.success("Review token marked as used:", id);
  } catch (error) {
    logger.error("Error marking token as used:", error);
    throw error;
  }
};

export const getActiveTokens = async (): Promise<ReviewToken[]> => {
  try {
    const tokensRef = collection(db, "reviewTokens");
    const q = query(tokensRef, orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as ReviewToken[];
  } catch (error) {
    logger.error("Error fetching active tokens:", error);
    return [];
  }
};

export const deleteReviewToken = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "reviewTokens", id);
    await deleteDoc(docRef);
    logger.success("Review token deleted:", id);
  } catch (error) {
    logger.error("Error deleting review token:", error);
    throw error;
  }
};

// Experience operations
export const getExperience = async (): Promise<Experience[]> => {
  try {
    const cached = cache.get<Experience[]>(CACHE_KEYS.EXPERIENCE);
    if (cached) {
      logger.info("Using cached experience data");
      return cached;
    }

    logger.info("Fetching experience from Firestore");
    const expRef = collection(db, "experience");

    try {
      const q = query(expRef, orderBy("order"));
      const querySnapshot = await getDocs(q);
      const experience = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Experience[];
      cache.set(CACHE_KEYS.EXPERIENCE, experience);
      return experience;
    } catch (orderError) {
      logger.warn("Experience orderBy failed, trying without ordering");
      const querySnapshot = await getDocs(expRef);
      const experience = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Experience[];
      experience.sort((a, b) => a.order - b.order);
      cache.set(CACHE_KEYS.EXPERIENCE, experience);
      return experience;
    }
  } catch (error) {
    logger.error("Error fetching experience:", error);
    return [];
  }
};

export const addExperience = async (exp: Omit<Experience, "id">): Promise<string> => {
  try {
    const expRef = collection(db, "experience");
    const docRef = await addDoc(expRef, {
      ...exp,
      createdAt: new Date().toISOString(),
    });
    cache.invalidate(CACHE_KEYS.EXPERIENCE);
    logger.success("Experience added:", docRef.id);
    return docRef.id;
  } catch (error) {
    logger.error("Error adding experience:", error);
    throw error;
  }
};

export const updateExperience = async (id: string, exp: Partial<Experience>): Promise<void> => {
  try {
    const docRef = doc(db, "experience", id);
    await updateDoc(docRef, exp);
    cache.invalidate(CACHE_KEYS.EXPERIENCE);
    logger.success("Experience updated:", id);
  } catch (error) {
    logger.error("Error updating experience:", error);
    throw error;
  }
};

export const deleteExperience = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, "experience", id);
    await deleteDoc(docRef);
    cache.invalidate(CACHE_KEYS.EXPERIENCE);
    logger.success("Experience deleted:", id);
  } catch (error) {
    logger.error("Error deleting experience:", error);
    throw error;
  }
};


// Theme tokens (admin-editable palette) — stored separately from Settings so
// palette edits can never clobber contact/social data.
export const getThemeTokens = async (): Promise<ThemeTokens | null> => {
  try {
    const docRef = doc(db, "settings", "theme");
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as ThemeTokens) : null;
  } catch (error) {
    logger.error("Error fetching theme tokens:", error);
    return null;
  }
};

export const updateThemeTokens = async (tokens: ThemeTokens): Promise<void> => {
  try {
    const docRef = doc(db, "settings", "theme");
    await setDoc(docRef, tokens);
    logger.success("Theme tokens updated");
  } catch (error) {
    logger.error("Error updating theme tokens:", error);
    throw error;
  }
};

// Niche site configuration (admin-editable overrides for constants/niches.ts)
export const getNicheOverrides = async (): Promise<Record<string, NicheOverride>> => {
  try {
    const snapshot = await getDocs(collection(db, "niches"));
    const result: Record<string, NicheOverride> = {};
    snapshot.docs.forEach((d) => {
      result[d.id] = d.data() as NicheOverride;
    });
    return result;
  } catch (error) {
    logger.error("Error fetching niche overrides:", error);
    return {};
  }
};

export const updateNicheOverride = async (
  key: string,
  data: NicheOverride
): Promise<void> => {
  try {
    await setDoc(doc(db, "niches", key), data, { merge: true });
    logger.success("Niche override updated:", key);
  } catch (error) {
    logger.error("Error updating niche override:", error);
    throw error;
  }
};
